import { Autocomplete, TextField } from '@evertrue/sdk';
import PropTypes from 'prop-types';

// Constants for styling consistency
const DROPDOWN_COLORS = {
  BACKGROUND: '#f9f9f9',
  BORDER: '#b7b7b7',
  FOCUS_BACKGROUND: '#fcf8fc',
  FOCUS_BORDER: '#ba72c5',
};

const DROPDOWN_CONFIG = {
  MAX_HEIGHT: '150px',
  OPTION_PADDING: '8px 16px',
  INPUT_PADDING: '4px',
  BORDER_RADIUS: '4px',
};

/**
 * Simple autocomplete component for server-side search
 */
const AutocompleteComponent = ({
  searchResults,
  isSearching = false,
  query = '',
  handleSearch,
  clearSearch,
  value,
  onSelect,
  placeholder = 'Search...',
  disabled = false,
}) => {
  const handleInputChange = (_event, newInputValue) => {
    if (newInputValue.length > 0) {
      handleSearch(newInputValue);
    } else {
      clearSearch();
    }
  };

  const handleChange = (_event, newValue) => {
    onSelect(newValue);
    if (!newValue) {
      clearSearch();
    }
  };

  return (
    <Autocomplete
      sx={{
        // Dropdown positioning and styling
        '& .MuiAutocomplete-listbox': {
          maxHeight: `${DROPDOWN_CONFIG.MAX_HEIGHT} !important`,
          overflowY: 'auto !important',
        },
      }}
      slotProps={{
        popper: {
          placement: 'bottom-start',
          modifiers: [
            {
              name: 'preventOverflow',
              enabled: false,
            },
            {
              name: 'flip',
              enabled: false,
            },
          ],
        },
      }}
      ListboxProps={{
        style: {
          maxHeight: DROPDOWN_CONFIG.MAX_HEIGHT,
          overflow: 'auto',
        },
      }}
      options={searchResults}
      getOptionLabel={(option) => option.title || option.label || ''}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      value={value || null}
      onChange={handleChange}
      onInputChange={handleInputChange}
      inputValue={query}
      loading={isSearching}
      disabled={disabled}
      filterOptions={(options) => options} // No client-side filtering, server handles search
      noOptionsText="No results found"
      renderOption={(liProps, option) => (
        <li
          {...liProps}
          style={{
            ...liProps.style,
            padding: DROPDOWN_CONFIG.OPTION_PADDING,
          }}
        >
          {option.label}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          variant="outlined"
          sx={{
            // Custom styling for consistent appearance
            '& .MuiOutlinedInput-root': {
              background: `${DROPDOWN_COLORS.BACKGROUND} !important`,
              border: `1px solid ${DROPDOWN_COLORS.BORDER} !important`,
              borderRadius: `${DROPDOWN_CONFIG.BORDER_RADIUS} !important`,

              // Remove default fieldset border
              '& fieldset': {
                border: 'none !important',
              },

              // Hover state maintains same appearance
              '&:hover': {
                background: `${DROPDOWN_COLORS.BACKGROUND} !important`,
                border: `1px solid ${DROPDOWN_COLORS.BORDER} !important`,
                '& fieldset': {
                  border: 'none !important',
                },
              },

              // Focus state with accent color
              '&.Mui-focused': {
                background: `${DROPDOWN_COLORS.FOCUS_BACKGROUND} !important`,
                border: `1px solid ${DROPDOWN_COLORS.FOCUS_BORDER} !important`,
                '& fieldset': {
                  border: 'none !important',
                },
              },
            },

            // Input element styling
            '& .MuiInputBase-input': {
              padding: `${DROPDOWN_CONFIG.INPUT_PADDING} !important`,
              background: 'transparent !important',
              border: 'none !important',
            },
          }}
        />
      )}
    />
  );
};

AutocompleteComponent.propTypes = {
  searchResults: PropTypes.array.isRequired,
  isSearching: PropTypes.bool,
  query: PropTypes.string,
  handleSearch: PropTypes.func.isRequired,
  clearSearch: PropTypes.func.isRequired,
  value: PropTypes.any,
  onSelect: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
};

export default AutocompleteComponent;
